/**
 * NotificationPreferencesSection — toggle matrix for notification event types × channels.
 *
 * Renders 5 event types × 4 channels as a grid of toggle switches.
 * On toggle change, calls PUT preferences endpoint. Reverts on failure with error message.
 * Displays confirmation on success.
 *
 * Requirements: 11.3, 11.4, 11.5
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import * as profileApi from '../api/user-profile';
import type { NotificationPreferences, ChannelToggles } from '../api/user-profile';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type EventType = keyof NotificationPreferences;
type Channel = keyof ChannelToggles;

const EVENT_TYPES: readonly { key: EventType; label: string }[] = [
  { key: 'card.assigned', label: 'Card assigned' },
  { key: 'board.invite.created', label: 'Board invitation' },
  { key: 'comment.mention', label: 'Comment mention' },
  { key: 'card.due.approaching', label: 'Due date approaching' },
  { key: 'card.moved', label: 'Card moved' },
] as const;

const CHANNELS: readonly { key: Channel; label: string }[] = [
  { key: 'inApp', label: 'In-App' },
  { key: 'email', label: 'Email' },
  { key: 'teams', label: 'Teams' },
  { key: 'slack', label: 'Slack' },
] as const;

const DEFAULT_PREFERENCES: NotificationPreferences = {
  'card.assigned': { inApp: true, email: false, teams: false, slack: false },
  'board.invite.created': { inApp: true, email: false, teams: false, slack: false },
  'comment.mention': { inApp: true, email: false, teams: false, slack: false },
  'card.due.approaching': { inApp: true, email: false, teams: false, slack: false },
  'card.moved': { inApp: true, email: false, teams: false, slack: false },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationPreferencesSection(): React.ReactElement {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch preferences on mount
  useEffect(() => {
    async function load(): Promise<void> {
      try {
        const prefs = await profileApi.getPreferences();
        setPreferences(prefs);
      } catch {
        // Use defaults if fetch fails
        setPreferences(DEFAULT_PREFERENCES);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  // Clear success message timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleToggle = useCallback(
    async (eventType: EventType, channel: Channel, checked: boolean): Promise<void> => {
      if (!preferences) return;

      const previousPreferences = preferences;
      const updatedPreferences: NotificationPreferences = {
        ...preferences,
        [eventType]: {
          ...preferences[eventType],
          [channel]: checked,
        },
      };

      // Optimistic update
      setPreferences(updatedPreferences);
      setError(null);
      setSuccessMessage(null);

      try {
        await profileApi.updatePreferences(updatedPreferences);
        setSuccessMessage('Preferences saved');
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err: unknown) {
        // Revert on failure
        setPreferences(previousPreferences);
        const message = err instanceof Error ? err.message : 'Failed to save preferences';
        setError(message);
      }
    },
    [preferences],
  );

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading notification preferences" className="py-4">
        <span className="text-sm text-muted-foreground">Loading preferences…</span>
      </div>
    );
  }

  if (!preferences) return <></>;

  return (
    <section aria-labelledby="notification-prefs-heading" className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose which notifications you receive and how they are delivered.
      </p>

      {/* Status messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Toggle matrix */}
      <div className="overflow-x-auto" role="table" aria-label="Notification preferences matrix">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="pb-2 pr-4 text-left font-medium" scope="col">
                Event
              </th>
              {CHANNELS.map((ch) => (
                <th key={ch.key} className="pb-2 px-3 text-center font-medium" scope="col">
                  {ch.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EVENT_TYPES.map((evt) => (
              <tr key={evt.key} className="border-b last:border-b-0">
                <td className="py-3 pr-4 font-medium">{evt.label}</td>
                {CHANNELS.map((ch) => {
                  const isChecked = preferences[evt.key][ch.key];
                  const toggleId = `pref-${evt.key}-${ch.key}`;
                  return (
                    <td key={ch.key} className="py-3 px-3 text-center">
                      <Checkbox
                        id={toggleId}
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          handleToggle(evt.key, ch.key, checked === true);
                        }}
                        aria-label={`${evt.label} via ${ch.label}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
